'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  CopyIdButton,
  DrawerCollapsible,
  DrawerGeneralDetails,
  HeadingItem,
  MarkdownPreview,
  MonoSmall,
  Scroll,
  Spinner,
  Tabs,
} from '@terra/ui-design-system';
import {
  AiApplicationMcpComponentDetails,
  RelatedAiComponentsDrawerTable,
  getAiComponentInputSchema,
  getAiComponentTypeIcon,
  isToolFamilyComponentType,
} from '@terra/ui-ai-applications';
import {
  resolveAiComponentDisplayDescription,
  type AiApplicationComponentsResponseDto,
  type AiComponentDto,
} from '@terra/shared-contracts-ai-applications';
import { AlertCircleIcon, Database01Icon, Link02Icon } from '@terra/ui-icons';
import type { AiComponentMainContentTab } from '../../store/layoutStore';
import { useTargetId } from '../../store/projectStore';
import { useAiApplicationComponents } from '../../hooks/api/useAiApplicationComponents';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { EmptyState } from '../EmptyState';
import {
  AiComponentCoverageBadge,
  AiComponentTypeBadge,
  AiComponentVerificationBadge,
} from './ai-assets/AiComponentsTableCells';

const MCP_COMPONENT_TYPES: ReadonlySet<AiComponentDto['type']> = new Set([
  'MCP_TOOL',
  'MCP_PROTOCOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
]);

type AiComponentRelation =
  AiApplicationComponentsResponseDto['relations'][number];

const resolveRelatedComponents = ({
  component,
  components,
  relations,
}: {
  component: AiComponentDto;
  components: readonly AiComponentDto[];
  relations: readonly AiComponentRelation[];
}): AiComponentDto[] => {
  const relatedIds = new Set(
    relations.flatMap((relation) =>
      relation.sourceId === component.id
        ? [relation.targetId]
        : relation.targetId === component.id
          ? [relation.sourceId]
          : [],
    ),
  );

  return components.filter((item) => relatedIds.has(item.id));
};

function AiComponentDetailsSection({
  component,
  relationshipCount,
}: {
  component: AiComponentDto;
  relationshipCount: number;
}) {
  const t = useTranslations('aiApplications.componentDrawer');
  const tShared = useTranslations('aiApplications.agentDrilldown');
  const dateFormatter = useDateFormatter();
  const toolInputSchema = getAiComponentInputSchema({
    metadata: component.metadata,
    runtimeOverlay: component.runtimeOverlay,
  });

  return (
    <VStack gap={8} alignItems="stretch">
      <DrawerGeneralDetails
        title={tShared('generalDetails')}
        rows={[
          [
            {
              title: tShared('type'),
              value: <AiComponentTypeBadge type={component.type} />,
            },
            {
              title: t('verificationStatus'),
              value: <AiComponentVerificationBadge component={component} />,
            },
          ],
          [
            {
              title: tShared('coverage'),
              value: (
                <AiComponentCoverageBadge isInternal={component.isInternal} />
              ),
            },
            {
              title: tShared('role'),
              value: component.role ?? tShared('unknownRole'),
            },
          ],
          [
            {
              title: t('provenance'),
              value: component.provenance
                ? t(`provenanceValues.${component.provenance}`)
                : tShared('unknownRole'),
            },
            {
              title: t('sources'),
              value:
                component.sources.length === 0
                  ? tShared('noInterface')
                  : component.sources
                      .map((source) => t(`sourceValues.${source}`))
                      .join(', '),
            },
          ],
          [
            {
              title: t('interfaceType'),
              value: component.interfaceType ?? tShared('noInterface'),
            },
            {
              title: tShared('relationships'),
              value: `${relationshipCount}`,
            },
          ],
          [
            {
              title: tShared('updated'),
              value: dateFormatter.format(new Date(component.updatedAt)),
            },
          ],
        ]}
      />
      {MCP_COMPONENT_TYPES.has(component.type) ? (
        <AiApplicationMcpComponentDetails component={component} />
      ) : null}
      {isToolFamilyComponentType(component.type) && toolInputSchema !== null ? (
        <DrawerCollapsible title={t('inputSchema')} defaultOpen>
          <MarkdownPreview
            source={`\`\`\`json\n${JSON.stringify(toolInputSchema, null, 2)}\n\`\`\``}
            compact
          />
        </DrawerCollapsible>
      ) : null}
    </VStack>
  );
}

export function AiComponentDetailTab({
  tab,
}: {
  tab: AiComponentMainContentTab;
}) {
  const targetId = useTargetId();
  const tAssetDetail = useTranslations('externalNetwork.assetDetail');
  const tAssetList = useTranslations('internalNetwork.assetList');
  const tAssetEntity = useTranslations('designSystem.signals.drawer');
  const [selectedTab, setSelectedTab] = useState('overview');
  const { components, relations, isLoading, isError } =
    useAiApplicationComponents({ aiApplicationId: targetId ?? null });
  const component = components.find((item) => item.id === tab.data.componentId);
  const relatedComponents = useMemo(
    () =>
      component
        ? resolveRelatedComponents({ component, components, relations })
        : [],
    [component, components, relations],
  );

  if (isLoading) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (isError || !component) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <EmptyState
          icon={isError ? AlertCircleIcon : Database01Icon}
          title={
            isError ? tAssetList('loadError') : tAssetDetail('notFoundTitle')
          }
          description={
            isError
              ? tAssetList('loadErrorDescription')
              : tAssetDetail('notFoundDescription')
          }
        />
      </Box>
    );
  }

  const Icon = getAiComponentTypeIcon(component.type);
  const description = resolveAiComponentDisplayDescription({
    description: component.description,
    metadata: component.metadata,
    type: component.type,
  });

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        <Box p="4" borderBottomWidth="1px" borderColor="border.subtle">
          <VStack gap="2" alignItems="flex-start" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <Box
                display="flex"
                alignItems="center"
                flexShrink={0}
                aria-hidden
              >
                <Icon size={20} />
              </Box>
              <AiComponentTypeBadge type={component.type} />
              <HeadingItem flex="1" minW={0} truncate title={component.name}>
                {component.name}
              </HeadingItem>
              <CopyIdButton
                id={component.id}
                entityName={tAssetEntity('asset')}
              />
              <AiComponentVerificationBadge component={component} />
            </HStack>
            {description ? (
              <BodyDefault color="fg.muted">{description}</BodyDefault>
            ) : null}
            {component.canonicalKey ? (
              <HStack gap="1" alignItems="center" w="full" minW={0}>
                <Link02Icon size={14} aria-hidden style={{ flexShrink: 0 }} />
                <MonoSmall color="fg.muted" truncate>
                  {component.canonicalKey}
                </MonoSmall>
              </HStack>
            ) : null}
          </VStack>
        </Box>

        <Box flex="1" overflow="hidden">
          <Tabs.Root
            value={selectedTab}
            size="sm"
            w="full"
            h="full"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            lazyMount
            unmountOnExit
            onValueChange={(details) => setSelectedTab(details.value)}
          >
            <Tabs.List justifyContent="flex-start" px={4}>
              <Tabs.Trigger value="overview" fontSize="sm" px="3" py="2">
                {tAssetDetail('tabs.details')}
              </Tabs.Trigger>
              <Tabs.Trigger value="relationships" fontSize="sm" px="3" py="2">
                {tAssetDetail('tabs.relationships')}
              </Tabs.Trigger>
              <Tabs.Indicator />
            </Tabs.List>

            <Tabs.Content value="overview" flex="1" overflow="hidden" pt="0">
              <Scroll orientation="vertical" showScrollbar h="full">
                <Box p={6}>
                  <AiComponentDetailsSection
                    component={component}
                    relationshipCount={relatedComponents.length}
                  />
                </Box>
              </Scroll>
            </Tabs.Content>

            <Tabs.Content
              value="relationships"
              flex="1"
              overflow="hidden"
              pt="0"
            >
              <Scroll orientation="vertical" showScrollbar h="full">
                <Box p={6}>
                  <RelatedAiComponentsDrawerTable
                    relatedComponents={relatedComponents}
                  />
                </Box>
              </Scroll>
            </Tabs.Content>
          </Tabs.Root>
        </Box>
      </VStack>
    </Box>
  );
}
